import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import classnames from 'classnames';
import { withStyles } from 'theme/withStyles';
import RawHtml from 'common/RawHtml';
import Button from 'common/Button';
import Group from 'containers/Group';
import ButtonRowGroup from 'groups/common/ButtonRowGroup';
import renderFields from 'utils/renderFields';
import Spinner from 'common/Spinner';
import ErrorMsg from 'common/ErrorMsg';
import commonGroupStyles from 'groups/styles';
import pageActions from 'core/page/actions';
import buttonRowStyles from 'groups/styles/buttonRow';
import SignerstatusRules from 'common/SignerstatusRules';
import rootStyles from './styles/root';

/**
 * This page component is tightly coupled to the groups PRIMARY_USER and BUSINESS_USERS
 */
const PRIMARY_USER_GROUP = 'PRIMARY_USER';
const BUSINESS_USERS_GROUP = 'BUSINESS_USERS';

const styles = (theme) => {
  const common = {
    ...commonGroupStyles(theme),
    root: rootStyles(theme),
  };
  return {
    '@global': {
      '#BUSINESS_USERS': {
        display: 'block',
        marginTop: 0,
      },
    },
    ...common,
    accordionContent: {
      textAlign: 'center',
    },
    accordionHandle: {
      display: 'block',
      margin: '0 auto',
      marginBottom: 15,
    },
    button: {
      margin: [theme.spacing.xs, 0],
    },
    buttonAddUser: {
      display: 'block',
      margin: '50px auto 0',
    },
    buttonRow: {
      ...buttonRowStyles(theme),
      display: 'flex',
      flexDirection: 'column-reverse',
      marginLeft: 'auto',
      marginRight: 'auto',
      width: 300,
    },
    content: {
      ...common.content,
      margin: [20, 0],
    },
    continueWithout: {
      margin: [105, 0, -27, 0],
      textAlign: 'center',
    },
    h2: {
      ...theme.typography.h1,
      marginBottom: 10,
      textAlign: 'center',
      [`@media(min-width: ${theme.screenSizes.mobileMax}px)`]: {
        marginBottom: 27,
      },
    },
  };
};

export const SignerstatusBare = ({
  businessRolesContent,
  businessUsersGroup,
  businessUsersList,
  classes,
  disableButton,
  handleAdd,
  headerHtml,
  hideBusinessRolesLabel,
  isEditable,
  loading,
  name,
  primaryUserGroup,
  saveSigners,
  showBusinesRolesLabel,
  subtitleEditable,
  subtitleReadOnly,
  titleEditable,
  titleReadOnly,
}) => {
  const [showAccordion, setShowAccordion] = useState(false);

  return (
    <div className={classnames('signerstatus-view', classes.root)}>
      <RawHtml>{headerHtml}</RawHtml>

      <ErrorMsg type="page" />

      <RawHtml type="h2" className={classnames('h2', classes.h2)}>
        {isEditable ? titleEditable : titleReadOnly}
      </RawHtml>

      <div className={classes.content}>
        <RawHtml>{isEditable ? subtitleEditable : subtitleReadOnly}</RawHtml>
      </div>

      {isEditable && showBusinesRolesLabel && hideBusinessRolesLabel && (
        <Button
          variant="secondary"
          className={classes.accordionHandle}
          onClick={() => setShowAccordion((state) => !state)}
        >
          {showAccordion ? hideBusinessRolesLabel : showBusinesRolesLabel}
        </Button>
      )}

      {isEditable && (showAccordion || (!hideBusinessRolesLabel && !showBusinesRolesLabel)) && (
        <RawHtml type="div" className={classes.accordionContent}>
          {businessRolesContent}
        </RawHtml>
      )}

      {primaryUserGroup?.name && (
        <Group key={PRIMARY_USER_GROUP} name={PRIMARY_USER_GROUP}>
          <div className="fields-view">{renderFields(primaryUserGroup)}</div>
        </Group>
      )}

      {businessUsersGroup?.name && businessUsersList.length > 0 ? (
        <Group key={BUSINESS_USERS_GROUP} name={BUSINESS_USERS_GROUP}>
          <div className="fields-view">{renderFields(businessUsersGroup)}</div>
        </Group>
      ) : (
        <>
          <SignerstatusRules />
          <Button
            className={classnames(
              'btn btn-mobile btn-tertiary btn-add-user',
              classes.buttonAddUser,
              {
                hidden: businessUsersGroup.resources?.hasMaxBusinessSigners === 'true',
              },
            )}
            data-name="btn-add-user"
            variant="secondary"
            onClick={handleAdd}
          >
            {businessUsersGroup.resources?.initialListAddButtonLabel}
          </Button>
        </>
      )}

      {businessUsersList.length === 0 && (
        <RawHtml type="p" className={classnames('continue-without', classes.continueWithout)}>
          {businessUsersGroup.resources?.continueWithoutSignersLabel}
        </RawHtml>
      )}

      {isEditable ? (
        <div className={classnames('row btn-row-group', `btn-group-${name}`, classes.buttonRow)}>
          <Button
            className={classnames('btn btn-mobile btn-grp-submit', { loading })}
            data-name="btn-grp-submit"
            onClick={saveSigners}
            disabled={disableButton}
          >
            Submit
            <i className="icon-arrow-thin-right" />
            <Spinner loading={loading} />
          </Button>
        </div>
      ) : (
        <ButtonRowGroup groupName={BUSINESS_USERS_GROUP} />
      )}
      <Spinner {...{ loading, name }} type="page" />
    </div>
  );
};

SignerstatusBare.propTypes = {
  backAllowed: PropTypes.bool,
  backButtonLabel: PropTypes.string,
  businessRolesContent: PropTypes.string,
  businessUsersGroup: PropTypes.shape({
    label: PropTypes.string,
    name: PropTypes.string,
    resources: PropTypes.shape({
      continueWithoutSignersLabel: PropTypes.string,
      hasMaxBusinessSigners: PropTypes.bool,
      initialListAddButtonLabel: PropTypes.string,
      subtext: PropTypes.string,
    }),
  }),
  businessUsersList: PropTypes.arrayOf(
    PropTypes.shape({
      isNew: PropTypes.bool,
    }),
  ),
  classes: PropTypes.objectOf(PropTypes.string).isRequired,
  disableButton: PropTypes.bool,
  handleAdd: PropTypes.func,
  headerHtml: PropTypes.string,
  hideBusinessRolesLabel: PropTypes.string,
  isEditable: PropTypes.bool,
  loading: PropTypes.bool,
  name: PropTypes.string,
  primaryUserGroup: PropTypes.shape({
    name: PropTypes.string,
  }),
  saveSigners: PropTypes.func,
  showBusinesRolesLabel: PropTypes.string,
  subtitleEditable: PropTypes.string,
  subtitleReadOnly: PropTypes.string,
  titleEditable: PropTypes.string,
  titleReadOnly: PropTypes.string,
};

SignerstatusBare.defaultProps = {
  backAllowed: false,
  backButtonLabel: null,
  businessRolesContent: '',
  businessUsersGroup: {},
  businessUsersList: [],
  disableButton: false,
  handleAdd: () => {},
  headerHtml: '',
  hideBusinessRolesLabel: '',
  isEditable: false,
  loading: false,
  name: null,
  primaryUserGroup: {},
  saveSigners: () => {},
  showBusinesRolesLabel: '',
  subtitleEditable: '',
  subtitleReadOnly: '',
  titleEditable: '',
  titleReadOnly: '',
};

const mapStateToProps = ({ data, groups, page, resources, session, ui }) => {
  const businessUsersGroup = groups[BUSINESS_USERS_GROUP];
  const primaryUserGroup = groups[PRIMARY_USER_GROUP];

  return {
    backAllowed: page.backAllowed,
    backButtonLabel: page.backButtonLabel,
    businessRolesContent: resources.businessRolesContent,
    businessUsersGroup,
    businessUsersList: data[`${BUSINESS_USERS_GROUP}___list`],
    disableButton:
      data?.[`${BUSINESS_USERS_GROUP}___list`] &&
      businessUsersGroup?.listGroup?.maxItems &&
      Object.values(data?.[`${BUSINESS_USERS_GROUP}___list`])?.length >
        businessUsersGroup?.listGroup?.maxItems,
    headerHtml: page.headerHtml,
    hideBusinessRolesLabel: resources.hideBusinessRolesLabel,
    isEditable: !data.needBackButton,
    loading: ui.pageLoading && session.meta.configs.page_spinny,
    name: page.name,
    primaryUserGroup,
    showBusinesRolesLabel: resources.showBusinesRolesLabel,
    subtitleEditable: resources.subtitleEditable,
    subtitleReadOnly: resources.subtitleReadOnly,
    titleEditable: resources.titleEditable,
    titleReadOnly: resources.titleReadOnly,
  };
};

const mapDispatchToProps = {
  handleAdd: pageActions.addSignerStatus,
  saveSigners: pageActions.saveSigners,
};

const SignerstatusStyled = withStyles(styles)(SignerstatusBare);

export default connect(mapStateToProps, mapDispatchToProps)(SignerstatusStyled);
